import { alpha, createTheme } from '@mui/material'
import { MuiLinearProgress } from './components-overrides'
import { resizableStyles } from './resizable-styles'

declare module '@mui/material' {
    interface ButtonBaseOwnProps {
        selected?: boolean
        onlyIcon?: boolean
        tab?: boolean
        badge?: boolean
        animated?: boolean
    }
    interface LinearProgressProps {
        meter?: boolean
        size?: 'small' | 'medium' | 'large'
    }
}

export const mainFont = 'Fira Sans'
export const secondaryFont = 'Space Grotesk'
export const buttonSize = {
    small: '32px',
    medium: '36px',
    large: '48px',
}

export const basicTheme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 640,
            md: 1024,
            lg: 1440,
            xl: 2048,
        },
    },
    palette: {
        mode: 'dark',
        common: {
            black: '#101820',
            white: '#FFFFFF',
        },
        primary: {
            light: '#C8F2F9',
            main: '#A5E9F6',
            dark: '#7BAFB8',
            contrastText: '#101820',
        },
        secondary: {
            light: '#FFEFC5',
            main: '#FFE49E',
            dark: '#CCB67E',
            contrastText: '#101820',
        },
        error: {
            light: '#FDCECA',
            main: '#FBAEA6',
            dark: '#BD837D',
            contrastText: '#101820',
        },
        background: {
            default: '#101820',
            paper: '#131D26',
        },
        text: {
            primary: '#FFFFFF',
            secondary: '#A5E9F6',
            disabled: '#8F8F8F',
        },
    },
})

let theme = basicTheme

theme = createTheme(theme, {
    typography: {
        fontFamily: [secondaryFont, 'Sans Serif'].join(','),
        h1: {
            fontSize: '60px',
            fontFamily: mainFont,
            fontWeight: 'bold',
            lineHeight: '60px',
        },
        h2: {
            fontSize: '40px',
            fontFamily: mainFont,
            fontWeight: 'bold',
            lineHeight: '42px',
        },
        h3: {
            fontSize: '36px',
            fontFamily: mainFont,
            fontWeight: 'bold',
            lineHeight: '38px',
        },
        h4: {
            fontSize: '32px',
            fontFamily: mainFont,
            fontWeight: 'bold',
            lineHeight: '34px',
        },
        h5: {
            fontSize: '24px',
            fontFamily: mainFont,
            fontWeight: 'bold',
            lineHeight: '26px',
        },
        h6: {
            fontSize: '18px',
            fontFamily: mainFont,
            fontWeight: 'bold',
            lineHeight: '24px',
        },
        body1: {
            fontSize: '18px',
            fontFamily: secondaryFont,
            fontWeight: 'regular',
            lineHeight: '20px',
        },
        body2: {
            fontSize: '14px',
            fontFamily: secondaryFont,
            fontWeight: 'regular',
            lineHeight: '16px',
        },
        button: {
            fontFamily: secondaryFont,
            fontWeight: 700,
            lineHeight: 'normal',
            textTransform: 'uppercase',
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                ...resizableStyles,
                '*': {
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${theme.palette.primary.main} transparent`,
                },
                body: {
                    userSelect: 'none',
                    '& .MuiDialog-root+.MuiDialog-root': {
                        display: 'none',
                    },
                },
                img: {
                    pointerEvents: 'none',
                    userSelect: 'none',
                },
                a: {
                    cursor: 'pointer',
                    color: 'inherit',
                    textDecoration: 'under',
                },
                html: {
                    fontSize: '16px',
                },

                'div[title="Choose promotion piece"]': {
                    backgroundColor: 'transparent !important',
                },

                'input[type=number]': {
                    MozAppearance: 'textfield',
                },
                'input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                },
                'input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                },
            },
        },
        MuiButton: {
            defaultProps: {
                size: 'medium',
                variant: 'outlined',
                color: 'primary',
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    boxSizing: 'border-box',
                    minWidth: '160px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    // '&.MuiButton-sizeLarge': {
                    //     border: `4px solid transparent`,
                    // },
                    ':hover:not(:active)': {
                        // backgroundColor: theme.palette.common.white,
                    },

                    ':active': {
                        borderColor: `${theme.palette.common.white}`,
                    },
                },
            },
            variants: [
                {
                    props: { variant: 'outlined' },
                    style: (props) => ({
                        backgroundColor: theme.palette.background.paper,
                        borderWidth: '2px !important',
                        borderColor: theme.palette.common.white + ' !important',
                        color: theme.palette.common.white,
                        ':hover:not(:active)': {
                            color: theme.palette[props.ownerState.color].main,
                        },
                        ':active': {
                            backgroundColor: theme.palette[props.ownerState.color].dark,
                            color: theme.palette.common.black,
                        },
                    }),
                },
                {
                    props: { size: 'large' },
                    style: {
                        height: buttonSize.large,
                        fontSize: `calc(${buttonSize.large} / 2)`,
                        svg: {
                            width: `calc(${buttonSize.large} / 1.5)`,
                            height: `calc(${buttonSize.large} / 1.5)`,
                        },
                    },
                },
                {
                    props: { size: 'medium' },
                    style: {
                        height: buttonSize.medium,
                        fontSize: `calc(${buttonSize.medium} / 2)`,
                        svg: {
                            width: `calc(${buttonSize.medium} / 1.5)`,
                            height: `calc(${buttonSize.medium} / 1.5)`,
                        },
                    },
                },
                {
                    props: { size: 'small' },
                    style: {
                        height: buttonSize.small,
                        fontSize: `calc(${buttonSize.small} / 2)`,
                        svg: {
                            width: `calc(${buttonSize.small} / 1.5)`,
                            height: `calc(${buttonSize.small} / 1.5)`,
                        },
                    },
                },
                {
                    props: { fullWidth: true },
                    style: {
                        minWidth: 'unset',
                    },
                },
                {
                    props: { onlyIcon: true },
                    style: {
                        '> span': {
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            margin: '0',
                        },
                        minWidth: 'unset',
                        width: 'fit-content',
                        aspectRatio: '1/1',
                        padding: '0 !important',
                        svg: {
                            width: '85%',
                            height: '85%',
                        },
                    },
                },
                {
                    props: { selected: true },
                    style: (props) => ({
                        backgroundColor: theme.palette[props.ownerState.color].main + `!important`,
                        borderColor: `transparent !important`,
                        color: theme.palette.common.black + ' !important',
                        cursor: 'default',
                    }),
                },
                {
                    props: { tab: true },
                    style: {
                        minWidth: 'unset',
                        borderRadius: '6px 6px 0 0',
                        borderColor: theme.palette.common.white + ' !important',
                        // borderBottom: 'none',
                    },
                },
                {
                    props: { badge: true },
                    style: {
                        position: 'relative',
                        '&:after': {
                            content: '"!"',
                            textAlign: 'center',
                            fontSize: '12px',
                            position: 'absolute',
                            color: theme.palette.common.white,
                            top: '0px',
                            right: '-1px',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: `#B3261E`,
                            filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.5))',
                            // boxShadow: `0 0 0 2px ${theme.palette.common.white}, 0 0 2px 3px ${theme.palette.error.main}`,
                            transform: 'translate(50%, -50%)',
                        },
                    },
                },
                {
                    props: { animated: true },
                    style: (props) => ({
                        '@keyframes icon-jiggle': {
                            '0%': { transform: 'rotate(0deg) scale(1)' },
                            '20%': { transform: 'rotate(0deg) scale(1.5)' },
                            '30%': { transform: 'rotate(-10deg) scale(1.5)' },
                            '40%': { transform: 'rotate(10deg) scale(1.50)' },
                            '50%': { transform: 'rotate(-10deg) scale(1.50)' },
                            '60%': { transform: 'rotate(10deg) scale(1.50)' },
                            '70%': { transform: 'rotate(-10deg) scale(1.50)' },
                            '80%': { transform: 'rotate(10deg) scale(1.50)' },
                            '100%': { transform: 'rotate(0deg) scale(1)' },
                        },
                        '@keyframes button-glow': {
                            '0%': {
                                boxShadow: `0 0 5px ${alpha(theme.palette.background.default, 0)})`,
                                filter: `drop-shadow(0 0 2px  ${alpha(theme.palette.background.default, 0)})`,
                            },
                            '50%': {
                                boxShadow: `0 0 10px ${alpha(theme.palette[props.ownerState.color].main, 0.7)}`,
                                filter: `drop-shadow(0 0 5px ${alpha(theme.palette[props.ownerState.color].main, 0.7)})`,
                            },
                            '100%': {
                                boxShadow: `0 0 5px ${alpha(theme.palette.background.default, 0)})`,
                                filter: `drop-shadow(0 0 2px  ${alpha(theme.palette.background.default, 0)})`,
                            },
                        },

                        position: 'relative', // Good for positioning pseudo-elements if needed for glow
                        transition: 'transform 0.2s ease-out, box-shadow 0.3s ease-in-out', // Smooth transitions for hover/focus
                        animation: `button-glow 0.75s ease-in-out 2`, // Jiggle once, glow infinitely with a slight delay

                        '&:hover': {
                            // Optional: Enhance glow or add a slight scale on hover
                            boxShadow: `0 0 15px ${alpha(theme.palette[props.ownerState.color].main, 0.7)}`,
                        },
                        // Targeting the SVG icon specifically for the glow's drop-shadow
                        '& svg': {
                            // Or your SVG's class if not using MuiSvgIcon directly
                            animation: `icon-jiggle 1.5s ease-in-out 1`,
                        },
                    }),
                },
                {
                    props: { disabled: true },
                    style: {
                        backgroundColor: '#585D63 !important',
                        color: '#878B8F !important',
                        border: 'none !important',
                    },
                },
            ],
        },
        MuiLinearProgress,
        MuiAvatar: {
            styleOverrides: {
                root: {
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: '12px',
                },
            },
        },
        MuiPaper: {
            defaultProps: {
                outlined: true,
                elevation: 0, // paper should have a default elevation of 0 so we can use it as a basis surface for many components
            },
            styleOverrides: {
                root: {
                    fill: theme.palette.text.primary,
                    backgroundImage: 'none',
                },
            },
            variants: [
                {
                    props: {
                        variant: 'outlined',
                    },
                    style: {
                        border: `2px solid ${theme.palette.common.white}`,
                        borderRadius: '12px',
                        padding: '12px',
                        boxShadow: 'none',
                        '.MuiButton-root': {
                            borderRadius: '6px',
                        },
                    },
                },
            ],
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {},
            },
        },
        MuiSlider: {
            styleOverrides: {
                valueLabel: {},
            },
        },

        MuiGrid: {
            defaultProps: {},
            styleOverrides: {
                root: {},
            },
        },

        MuiMobileStepper: {
            styleOverrides: {
                root: {
                    backgroundColor: theme.palette.background.paper,
                    '.MuiMobileStepper-dotActive': {
                        height: '8px !important',
                        width: '8px !important',
                    },
                    '.MuiMobileStepper-dots': {
                        alignItems: 'center',
                        gap: 10,
                    },
                    '.MuiMobileStepper-dot': {
                        height: '4px',
                        width: '4px',
                        margin: 0,
                        backgroundColor: theme.palette.secondary.main,
                    },
                },
            },
        },

        // DELETE
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: 0,
                    // marginTop: '32px',
                },
            },
        },

        MuiDialog: {
            styleOverrides: {
                root: {
                    '& .MuiDialog-paper': {
                        borderRadius: 0,
                        maxWidth: 'unset',
                        flexDirection: 'unset',
                        margin: '1rem',
                    },
                },
            },
        },
    },
})

export default theme
